import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";

import { cn } from "../../lib/cn";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-[color,background-color,transform,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 aria-busy:cursor-progress",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        /** The one next step in a view (SP-032); use at most one per screen. */
        highlight:
          "bg-highlight text-highlight-foreground font-bold shadow-[0_4px_0_rgb(0_0_0/0.18)] hover:-translate-y-px active:translate-y-0.5 active:shadow-[0_2px_0_rgb(0_0_0/0.18)] motion-reduce:transform-none",
        /** Secondary action on the brand band. */
        onBand:
          "border-2 border-band-foreground/50 text-band-foreground hover:bg-band-foreground/10",
        secondary: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        danger: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-[0.625rem] px-6 text-base",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows the action is in flight and blocks a second click. */
  readonly busy?: boolean;
  /** Renders the child element with the button's styling (e.g. a router Link). */
  readonly asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  busy = false,
  asChild = false,
  type,
  disabled,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      type={asChild ? undefined : (type ?? "button")}
      className={cn(buttonVariants({ variant, size }), className)}
      aria-busy={busy || undefined}
      disabled={busy || disabled}
      {...props}
    />
  );
}
